import React, { useEffect, useState } from "react";
import { usersApi, rolesApi, errorMessage } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Card, Table, Badge, Button, Modal, Loading, ErrorState, PageHeader, Input, Select, Pagination } from "../components/ui";
import { userStatusColor, userStatusLabel, formatDate } from "../format";

// Status yang boleh di-set MANUAL oleh admin. 'pending' (akun ke-sync tapi belum
// pernah login) dikelola sistem — otomatis jadi 'active' saat login — jadi tak
// masuk dropdown ini, tapi tetap tampil di badge lewat userStatusLabel.
const STATUSES = ["active", "inactive", "suspended"];
const PAGE_SIZE = 20;

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // untuk dropdown atasan (semua user, lintas halaman)
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState(null);

  const load = async (pageArg = 0, searchArg = search) => {
    setLoading(true);
    setError(null);
    try {
      const [u, r, all] = await Promise.all([
        usersApi.list({ search: searchArg || undefined, limit: PAGE_SIZE, offset: pageArg * PAGE_SIZE }),
        rolesApi.list().catch(() => []),
        usersApi.list({ limit: 1000 }).catch(() => ({ rows: [] })), // semua user utk dropdown atasan
      ]);
      setRows(u.rows || []);
      setTotal(u.total ?? (u.rows || []).length);
      setPage(pageArg);
      setRoles(r.rows || r || []);
      setAllUsers(all.rows || []);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { key: "username", header: "Username (barcode)", render: (u) => <span className="font-medium text-slate-800">{u.username}</span> },
    { key: "name", header: "Nama Karyawan", render: (u) => u.employee_name || "-" },
    { key: "nik", header: "NIK / Badge", render: (u) => u.badge_number || u.nik || "-" },
    { key: "role", header: "Role", render: (u) => <Badge color="blue">{u.role_name || u.role}</Badge> },
    { key: "status", header: "Status", render: (u) => <Badge color={userStatusColor(u.status)}>{userStatusLabel(u.status)}</Badge> },
    { key: "login", header: "Login Terakhir", render: (u) => (u.last_login ? <span className="text-xs whitespace-nowrap">{formatDate(u.last_login)}</span> : <span className="text-xs text-slate-400">belum</span>) },
    { key: "manager", header: "Atasan", render: (u) => u.manager_name || u.manager_username || "-" },
    { key: "pic", header: "PIC", render: (u) => u.pic_name || u.pic_username || "-" },
    {
      key: "act",
      header: "",
      render: (u) => <Button size="sm" variant="secondary" onClick={() => setManaging(u)}>Kelola</Button>,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Akun & Hierarki"
        subtitle="Kelola akun login, role, status, atasan (hierarki manajer), dan PIC (coach)."
        action={<Button onClick={() => setCreating(true)}>+ Buat Akun</Button>}
      />

      <div className="flex gap-2 mb-3 shrink-0">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(0, search)}
          placeholder="Cari username / nama…"
          className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        <Button variant="secondary" onClick={() => load(0, search)}>Cari</Button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(page)} />
        ) : (
          <>
            <Card className="flex-1 min-h-0 overflow-hidden">
              <Table columns={columns} rows={rows} empty="Belum ada akun." />
            </Card>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={(p) => load(p, search)} />
          </>
        )}
      </div>

      <CreateUserModal open={creating} roles={roles} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(page); }} />
      <ManageUserModal user={managing} roles={roles} users={allUsers.length ? allUsers : rows} onClose={() => setManaging(null)} onChanged={() => load(page)} />
    </div>
  );
}

function CreateUserModal({ open, roles, onClose, onDone }) {
  const toast = useAdminToast();
  const [form, setForm] = useState({ username: "", password: "", role: "EMPLOYEE" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ username: "", password: "", role: "EMPLOYEE" });
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { username: form.username, role: form.role };
      if (form.password) payload.password = form.password;
      const res = await usersApi.create(payload);
      if (res.temp_password) toast.success(`Akun dibuat. Password sementara: ${res.temp_password}`);
      else toast.success("Akun dibuat");
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Buat Akun Login"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy || !form.username}>{busy ? "Membuat…" : "Buat"}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Username (NIK)" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} />
        <Input
          label="Password / PIN (kosongkan untuk digenerate)"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          placeholder="min. 4 karakter"
        />
        <Select label="Role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
          {roles.map((r) => (
            <option key={r.id || r.code} value={r.code}>{r.name} ({r.code})</option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}

function ManageUserModal({ user, roles, users, onClose, onChanged }) {
  const toast = useAdminToast();
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [managerId, setManagerId] = useState("");
  const [picId, setPicId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setRole(user.role);
      setStatus(user.status);
      setManagerId(user.manager_id || "");
      setPicId(user.pic_id || "");
    }
  }, [user]);

  if (!user) return null;

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async () => {
    const np = window.prompt("Password/PIN baru (kosongkan untuk digenerate otomatis):", "");
    if (np === null) return;
    setBusy(true);
    try {
      const res = await usersApi.resetPassword(user.id, np || undefined);
      toast.success(res.temp_password ? `Password sementara: ${res.temp_password}` : "Password direset");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!user} onClose={onClose} title={`Kelola: ${user.username}`}>
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)} className="flex-1">
            {roles.map((r) => (
              <option key={r.id || r.code} value={r.code}>{r.name}</option>
            ))}
          </Select>
          <Button variant="secondary" disabled={busy || role === user.role} onClick={() => act(() => usersApi.setRole(user.id, role), "Role diperbarui")}>Simpan</Button>
        </div>

        <div className="flex items-end gap-2">
          <Select label="Status" value={STATUSES.includes(status) ? status : "active"} onChange={(e) => setStatus(e.target.value)} className="flex-1">
            {STATUSES.map((s) => (
              <option key={s} value={s}>{userStatusLabel(s)}</option>
            ))}
          </Select>
          <Button variant="secondary" disabled={busy || status === user.status} onClick={() => act(() => usersApi.setStatus(user.id, status), "Status diperbarui")}>Simpan</Button>
        </div>

        <div className="flex items-end gap-2">
          <Select label="Atasan (manager)" value={managerId} onChange={(e) => setManagerId(e.target.value)} className="flex-1">
            <option value="">— tidak ada —</option>
            {users.filter((u) => u.id !== user.id).map((u) => (
              <option key={u.id} value={u.id}>{u.employee_name || u.username} — {u.role} ({u.badge_number || u.username})</option>
            ))}
          </Select>
          <Button variant="secondary" disabled={busy || (managerId || "") === (user.manager_id || "")} onClick={() => act(() => usersApi.setManager(user.id, managerId || null), "Atasan diperbarui")}>Simpan</Button>
        </div>

        <div className="flex items-end gap-2">
          <Select label="PIC (coach — role PIC, terpisah dari atasan)" value={picId} onChange={(e) => setPicId(e.target.value)} className="flex-1">
            <option value="">— tidak ada —</option>
            {users.filter((u) => u.id !== user.id).map((u) => (
              <option key={u.id} value={u.id}>{u.employee_name || u.username} — {u.role} ({u.badge_number || u.username})</option>
            ))}
          </Select>
          <Button variant="secondary" disabled={busy || (picId || "") === (user.pic_id || "")} onClick={() => act(() => usersApi.setPic(user.id, picId || null), "PIC diperbarui")}>Simpan</Button>
        </div>

        <div className="pt-2 border-t border-slate-200">
          <Button variant="ghost" onClick={resetPw} disabled={busy}>🔑 Reset Password/PIN</Button>
        </div>
      </div>
    </Modal>
  );
}
